import React, { useState } from "react";
import { Box, Typography, Divider } from "@mui/material";
import SectionPaper from "./SectionPaper";
import DiamondTags from "./DiamondTags";
import GroupTitle from "./GroupTitle";
import SpecRow from "./SpecRow";
import SyncIcon from "@mui/icons-material/Sync";
import NavButton from "./NavButton";
import ActionButton from "./ActionButton";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { isGraded } from "../../backend/calibration";
import { seesGrading } from "../../app/features/auth/authTypes";
import { backendURL } from "../../constants";
import { SYNC_DIAMOND, tokenOptions } from "../../backend/graphql";
import { useAppSelector } from "../../app/hooks";
import { useMutation } from "@apollo/client";
import {
  asUTC,
  formatColorQuery,
  formatDate,
  formatPrice,
  formatPricePerCarat,
} from "../../tools";
import PricingMark from "./PricingMark";
import ColorValue from "./ColorValue";

// The analysis pages of a stone; each header leads to the others.
export type AnalysisPage = "image" | "price" | "grading";

type DiamondProfileProps = {
  data: any;
  // The page this header heads: its own button is left out.
  page: AnalysisPage;
};

const EM_DASH = "—";

const DiamondProfile = ({ data, page }: DiamondProfileProps) => {
  const token = useAppSelector((state) => state.auth.token);
  const role = useAppSelector((state) => state.auth.role);
  const isDeveloper = useAppSelector((state) => state.auth.isDeveloper);
  const [requested, setRequested] = useState<boolean>(false);
  const [syncDiamond] = useMutation(SYNC_DIAMOND, tokenOptions(token));

  // Sync re-pulls the stone from Fantasy and recalibrates in place. Reload once it finishes: a
  // fresh page re-requests every image, and the calibrated/thumbnail endpoints revalidate their
  // content ETag, so the recalibrated bitmaps replace the cached ones. Refetching the queries
  // alone would not — an <img> whose src (the stable image id) is unchanged never re-requests.
  const handleRefresh = async () => {
    setRequested(true);
    await syncDiamond({ variables: { id: data.id } });
    window.location.reload();
  };

  const handlePricing = () => {
    window.open("/analytics/diamondpricing/" + data.id, "_blank");
  };

  // Open the image analysis for this stone — its main image if there is one, else the diamond.
  const handleImageAnalysis = () => {
    window.open(
      data.mainImageID
        ? "/analytics/fromimage/" + data.mainImageID
        : "/analytics/fromdiamond/" + data.id,
      "_blank",
    );
  };

  const handleGrading = () => {
    window.open("/analytics/grading/" + data.id, "_blank");
  };

  const isLangerman = data.source === "Langerman";

  const reviewed = data.lastFantasyUpdate
    ? formatDate(asUTC(data.lastFantasyUpdate))
    : EM_DASH;

  // The grading analysis, for whoever sees it (seesGrading), on a stone with a picture to grade.
  const canGrade =
    seesGrading(role, isDeveloper) &&
    isLangerman &&
    (data.images ?? []).some((image: any) => isGraded(image.calibration));

  const canPrice =
    data.price &&
    data.clarity &&
    data.clarity !== "Unknown" &&
    data.color &&
    data.color !== "Unknown" &&
    (!data.tags || !data.tags.includes("BOX"));

  return (
    <SectionPaper
      sx={{
        p: "14px 18px",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        {/* The stone's representation image — the same one shown in the grid. Not clickable. */}
        {data.representationImageID && (
          <Box
            component="img"
            src={`${backendURL}/image/thumbnail/${data.representationImageID}`}
            alt=""
            sx={{
              width: 64,
              height: 64,
              flex: "none",
              objectFit: "contain",
              alignSelf: "flex-start",
              bgcolor: "#fff",
              borderRadius: 1,
            }}
          />
        )}

        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Identity, with the stone-level Sync beside it. */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "20px" }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: "1.5rem", fontWeight: 500, lineHeight: 1.2 }}
              >
                {data.source} {data.sKU}
              </Typography>
              <DiamondTags data={data} />
            </Box>
            {/* Stone-level actions, top-right: Sync, and the stone's other analysis pages. */}
            <Box
              sx={{ flex: "none", display: "flex", gap: 1, flexWrap: "wrap" }}
            >
              {isLangerman && (
                <ActionButton
                  startIcon={<SyncIcon />}
                  onClick={handleRefresh}
                  disabled={requested}
                >
                  Sync
                </ActionButton>
              )}
              {page !== "price" && canPrice ? (
                <NavButton
                  startIcon={<MonetizationOnIcon />}
                  onClick={handlePricing}
                  disabled={requested}
                >
                  Price analysis
                </NavButton>
              ) : null}
              {page !== "image" ? (
                <NavButton
                  startIcon={<TroubleshootIcon />}
                  onClick={handleImageAnalysis}
                  disabled={requested}
                >
                  Image analysis
                </NavButton>
              ) : null}
              {page !== "grading" && canGrade ? (
                <NavButton
                  startIcon={<LeaderboardIcon />}
                  onClick={handleGrading}
                  disabled={requested}
                >
                  Grading analysis
                </NavButton>
              ) : null}
            </Box>
          </Box>

          <Divider sx={{ my: "14px" }} />

          {/* stone | rule | color | gap | rule | price, so the columns line up at any width. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "minmax(0,1fr) 1px minmax(0,1fr) 20px 1px 200px",
            }}
          >
            <Box sx={{ gridColumn: 1, pr: 3 }}>
              <GroupTitle>Stone</GroupTitle>
              <SpecRow label="Shape" value={data.shape} />
              <SpecRow label="Cut" value={data.cut} />
              <SpecRow label="Carat" value={data.carat} />
              <SpecRow label="Clarity" value={data.clarity} />
            </Box>

            <Box
              sx={{ gridColumn: 2, borderLeft: 1, borderColor: "divider" }}
            />

            <Box sx={{ gridColumn: 3, pl: 3 }}>
              <GroupTitle>Color</GroupTitle>
              <SpecRow
                label="GIA"
                value={<ColorValue label={data.color} colorKey={data.color} />}
              />
              {isLangerman && (
                <>
                  <SpecRow
                    label="Langerman"
                    value={
                      <ColorValue
                        label={data.langermanColor}
                        colorKey={formatColorQuery(
                          "Langerman",
                          data.langermanColor,
                        )}
                      />
                    }
                  />
                  <SpecRow
                    label="Flat"
                    value={
                      <ColorValue
                        label={data.flat}
                        colorKey={`Flat ${data.flat}`}
                      />
                    }
                  />
                  <SpecRow
                    label="Melee"
                    value={
                      <ColorValue
                        label={data.melee}
                        colorKey={`Melee ${data.melee}`}
                      />
                    }
                  />
                </>
              )}
            </Box>

            <Box
              sx={{ gridColumn: 5, borderLeft: 1, borderColor: "divider" }}
            />

            <Box sx={{ gridColumn: 6, pl: "20px", textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                }}
              >
                Price
              </Typography>
              <Typography
                sx={{ fontSize: "1.75rem", fontWeight: 500, lineHeight: 1.3 }}
              >
                {data.price ? formatPrice(data.price) : EM_DASH}
              </Typography>
              {data.price && data.carat ? (
                <Typography
                  sx={{ color: "text.secondary", fontSize: "0.875rem" }}
                >
                  {formatPricePerCarat(data.price / data.carat)}
                </Typography>
              ) : null}
              {/* Where that price sits among comparable stones, as the grids' Pricing column
                  shows it, at the size of the price. */}
              <PricingMark
                percentile={data.currentPricePercentile}
                sx={{ fontSize: "1.75rem", lineHeight: 1.3 }}
              />
            </Box>

            {/* Pricing check runs across the bottom, Langerman-only; the remark starts on the
                color column. */}
            {isLangerman && (
              <>
                <Divider
                  sx={{ gridColumn: "1 / -1", mt: "14px", mb: "10px" }}
                />
                <Box sx={{ gridColumn: "1 / -1", mb: 1 }}>
                  <GroupTitle>Pricing Check</GroupTitle>
                </Box>
                <Box sx={{ gridColumn: 1 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.875rem",
                      color: "text.secondary",
                      mr: 1,
                    }}
                  >
                    Reviewed
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.9375rem", color: "text.secondary" }}
                  >
                    {reviewed}
                  </Typography>
                </Box>
                <Box sx={{ gridColumn: "3 / -1", pl: 3 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.875rem",
                      color: "text.secondary",
                      mr: 1,
                    }}
                  >
                    Remark
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.9375rem", color: "text.secondary" }}
                  >
                    {data.pricingRemark || EM_DASH}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </SectionPaper>
  );
};

export default DiamondProfile;
